import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Copy, Shield, Activity, LogOut, Check, Settings } from 'lucide-react';

export default function Dashboard({ session, supabase }) {
  const [workflows, setWorkflows] = useState([]);
  const [name, setName] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  // Integration Settings State
  const [showSettings, setShowSettings] = useState(false);
  const [integration, setIntegration] = useState({ n8n_url: '', n8n_api_key: '' });

  // Update: Ensure protocol is included for valid links
  const BACKEND_URL = "https://automation-watchdog-production.up.railway.app";

  const fetchWorkflows = async () => {
    const { data } = await supabase
      .from('workflows')
      .select('*')
      .order('created_at', { ascending: false });
    setWorkflows(data || []);
  };

  const fetchSettings = async () => {
    const { data } = await supabase
      .from('integrations')
      .select('n8n_url, n8n_api_key')
      .eq('user_id', session.user.id)
      .single();
    if (data) setIntegration(data);
  };

  useEffect(() => {
    fetchWorkflows();
    fetchSettings();
  }, []);

  const saveIntegration = async () => {
    const { error } = await supabase
      .from('integrations')
      .upsert({
        user_id: session.user.id,
        ...integration
      }, { onConflict: 'user_id' });

    if (error) {
      console.error("Supabase Error Details:", error.message);
      alert(`Error: ${error.message}`);
    } else {
      setShowSettings(false);
      alert("Settings saved successfully!");
    }
  };

  const handleCopy = (slug, id) => {
    const fullUrl = `${BACKEND_URL}/webhook/${slug}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const addWorkflow = async (e) => {
    e.preventDefault();
    const slug = `wp-${Math.random().toString(36).substr(2, 9)}`;
    const { error } = await supabase.from('workflows').insert([
      { name, slug, user_id: session.user.id, interval_mins: 10 }
    ]);
    if (!error) {
      setName('');
      setShowForm(false);
      fetchWorkflows();
    }
  };

  const deleteWorkflow = async (id) => {
    if (window.confirm("Are you sure you want to delete this monitor?")) {
      const { error } = await supabase
        .from('workflows')
        .delete()
        .eq('id', id);

      if (!error) {
        setWorkflows(workflows.filter(wf => wf.id !== id));
      } else {
        alert("Error deleting workflow: " + error.message);
      }
    }
  };

  const formatLastPing = (timestamp) => {
    if (!timestamp) return "Never";
    const diff = Math.floor((new Date() - new Date(timestamp)) / 1000 / 60);
    if (diff < 1) return "Just now";
    if (diff < 60) return `${diff}m ago`;
    return `${Math.floor(diff / 60)}h ago`;
  };

  return (
    <div className="min-h-screen bg-[#0d1117] text-[#c9d1d9] font-sans selection:bg-blue-500/30">
      {/* 1. NAVIGATION */}
      <nav className="border-b border-white/5 bg-[#161b22]/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 h-16 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="bg-blue-600/20 p-1.5 rounded-lg border border-blue-500/20">
              <Shield className="text-blue-500" size={20} />
            </div>
            <span className="font-bold text-white text-lg tracking-tight">Watchdog</span>
          </div>

          <div className="flex items-center gap-6">
            <button
              onClick={() => setShowSettings(true)}
              className="text-[#8b949e] hover:text-white transition-colors p-1"
            >
              <Settings size={20} />
            </button>

            <button onClick={() => supabase.auth.signOut()} className="group flex items-center gap-2 text-sm text-[#8b949e] hover:text-white transition-colors">
              <LogOut size={16} className="group-hover:translate-x-1 transition-transform" /> Logout
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
          <div>
            <h2 className="text-4xl font-extrabold text-white mb-2 tracking-tight">Systems Overview</h2>
            <p className="text-[#8b949e] text-lg">Active heartbeats protecting your automations.</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 transition-all shadow-lg shadow-blue-500/20 active:scale-95"
          >
            <Plus size={20} /> New Monitor
          </button>
        </div>

        {/* 2. ADD FORM */}
        {showForm && (
          <div className="mb-10 animate-in fade-in slide-in-from-top-4 duration-500">
            <form onSubmit={addWorkflow} className="bg-[#161b22] border border-white/10 p-8 rounded-3xl shadow-2xl ring-1 ring-blue-500/20">
              <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-blue-500 mb-4">Monitor Configuration</label>
              <div className="flex flex-col md:flex-row gap-4">
                <input
                  className="bg-[#0d1117] flex-1 p-4 rounded-xl border border-white/5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all placeholder:text-[#484f58]"
                  placeholder="e.g. Stripe Webhook Monitor"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
                <button className="bg-white text-black px-10 rounded-xl font-black hover:bg-gray-100 transition-colors shadow-lg">Activate</button>
              </div>
            </form>
          </div>
        )}

        {/* 3. MONITOR LIST */}
        <div className="grid grid-cols-1 gap-6">
          {workflows.map(wf => (
            <div key={wf.id} className="group bg-[#161b22] border border-white/5 rounded-3xl p-8 hover:border-white/20 hover:bg-[#1c2128] transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
              <div className="flex items-start justify-between mb-8">
                <div className="flex items-center gap-5">
                  <div className="relative">
                    <div className={`h-3 w-3 rounded-full ${wf.status === 'active' ? 'bg-green-500 shadow-[0_0_15px_rgba(34,197,94,0.6)]' : 'bg-red-500 shadow-[0_0_15px_rgba(239,68,68,0.6)]'}`} />
                    {wf.status === 'active' && <div className="absolute inset-0 h-3 w-3 rounded-full bg-green-500 animate-ping opacity-20" />}
                  </div>
                  <h3 className="text-2xl font-bold text-white tracking-tight">{wf.name}</h3>
                </div>
                {/* Trash Button hooked to delete function */}
                <button 
                  onClick={() => deleteWorkflow(wf.id)}
                  className="text-[#484f58] hover:text-red-400 transition-colors p-2 hover:bg-red-400/10 rounded-lg"
                >
                  <Trash2 size={20} />
                </button>
              </div>

              <div className="bg-[#0d1117]/50 rounded-2xl p-5 border border-white/5 group-hover:border-blue-500/20 transition-colors">
                <p className="text-[10px] font-black text-[#8b949e] uppercase tracking-[0.25em] mb-3 flex justify-between">
                  <span>Webhook Endpoint</span>
                  {copiedId === wf.id && <span className="text-blue-500 normal-case animate-pulse flex items-center gap-1"><Check size={12} /> Copied!</span>}
                </p>
                <div className="flex items-center justify-between gap-4">
                  <code className="text-blue-400 font-mono text-sm truncate bg-blue-500/5 px-3 py-1 rounded-md border border-blue-500/10">
                    {BACKEND_URL}/webhook/{wf.slug}
                  </code>
                  <button
                    onClick={() => handleCopy(wf.slug, wf.id)}
                    className="p-3 bg-[#21262d] hover:bg-[#30363d] rounded-xl transition-all text-[#c9d1d9] active:scale-90"
                  >
                    {copiedId === wf.id ? <Check size={18} className="text-blue-500" /> : <Copy size={18} />}
                  </button>
                </div>
              </div>

              {/* Status Footer with dynamic timing */}
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-white/5">
                <div className="flex items-center gap-2 text-xs text-[#8b949e]">
                  <Activity size={14} />
                  <span>Check interval: {wf.interval_mins}m</span>
                </div>
                <span className="text-xs font-mono text-[#8b949e]">
                  Last heartbeat: <span className="text-white">{formatLastPing(wf.last_ping_at)}</span>
                </span>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* 4. SETTINGS MODAL */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-6 animate-in fade-in duration-200">
          <div className="bg-[#161b22] border border-white/10 p-8 rounded-3xl max-w-md w-full shadow-2xl ring-1 ring-white/5">
            <h3 className="text-2xl font-bold text-white mb-6">Integration Settings</h3>
            <div className="space-y-6">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-[#8b949e] mb-2">n8n Instance URL</label>
                <input
                  className="bg-[#0d1117] w-full p-4 rounded-xl border border-white/5 focus:border-blue-500 outline-none transition-all"
                  placeholder="https://n8n.yourdomain.com"
                  value={integration.n8n_url}
                  onChange={(e) => setIntegration({ ...integration, n8n_url: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-[#8b949e] mb-2">n8n API Key</label>
                <input
                  type="password"
                  className="bg-[#0d1117] w-full p-4 rounded-xl border border-white/5 focus:border-blue-500 outline-none transition-all"
                  placeholder="N8N_API_KEY_..."
                  value={integration.n8n_api_key}
                  onChange={(e) => setIntegration({ ...integration, n8n_api_key: e.target.value })}
                />
              </div>
              <div className="flex gap-4 pt-4">
                <button
                  onClick={() => setShowSettings(false)}
                  className="flex-1 py-4 rounded-xl font-bold bg-[#21262d] hover:bg-[#30363d] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveIntegration}
                  className="flex-1 py-4 rounded-xl font-bold bg-blue-600 hover:bg-blue-500 transition-colors shadow-lg shadow-blue-500/20"
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}